import React from 'react';
import { Select } from '../../ui/components/Select';

/**
 * Valuation Strategy Selection Guide
 *
 * Choose based on collection characteristics:
 *
 * | Collection Type                    | Recommended Strategy |
 * |------------------------------------|---------------------|
 * | Active trading, unique rankings    | DEFAULT             |
 * | <50 NFTs or <10 sales/month        | FLOOR_ONLY          |
 * | Many #1 ranks (duplicate rarity)   | PERCENTILE_RANK     |
 * | Established with sale history      | SALES_HISTORY       |
 * | Volatile/speculative collections   | CONSERVATIVE        |
 * | Traits drive value, not rarity     | TRAIT_DEMAND        |
 * | Art/non-tradeable collections      | NONE                |
 */

/**
 * Valuation strategy options for the dropdown.
 * These match the PostgreSQL enum values.
 * Ordered by most common usage.
 */
const VALUATION_STRATEGIES = [
  {
    value: 'DEFAULT',
    label: 'Default (Multi-Factor)',
    description: 'Full model: baseline, traits, liquidity, trends. Best for active collections with unique rankings.',
    useWhen: 'Active trading, unique rarity rankings',
  },
  {
    value: 'FLOOR_ONLY',
    label: 'Floor Only (Small Collections)',
    description: 'Simple floor × (1 + 0-10% rank adjustment). Stable, conservative estimates.',
    useWhen: '<50 NFTs or <10 sales/month',
  },
  {
    value: 'PERCENTILE_RANK',
    label: 'Percentile Rank (Duplicate Rankings)',
    description: 'Percentile-based multipliers (Top 1%=2x, 5%=1.5x). Handles tied ranks fairly.',
    useWhen: 'Many NFTs share the same rank (duplicate rarity)',
  },
  {
    value: 'SALES_HISTORY',
    label: 'Sales History (Established)',
    description: '95% weight on individual NFT sales history. Falls back to collection median.',
    useWhen: 'Established collections with individual price history',
  },
  {
    value: 'CONSERVATIVE',
    label: 'Conservative (2x Floor Cap)',
    description: 'Same as Default but capped at 2x floor. Prevents extreme valuations.',
    useWhen: 'Volatile or speculative markets',
  },
  {
    value: 'TRAIT_DEMAND',
    label: 'Trait Demand (Market Signal)',
    description: 'Only uses trait sale premiums, ignores rarity scores entirely.',
    useWhen: 'Specific traits drive value more than rarity',
  },
  {
    value: 'NONE',
    label: 'No Valuation',
    description: 'Disables valuation calculation. Estimated values will be NULL.',
    useWhen: 'Art collections or where valuation is inappropriate',
  },
] as const;

export type ValuationStrategy = typeof VALUATION_STRATEGIES[number]['value'];

interface ValuationStrategySelectProps {
  value: ValuationStrategy;
  onChange: (strategy: ValuationStrategy) => void;
  disabled?: boolean;
  className?: string;
}

/**
 * Dropdown component for selecting a valuation calculation strategy.
 * Used in the admin collection management interface.
 */
export function ValuationStrategySelect({
  value,
  onChange,
  disabled = false,
  className = '',
}: ValuationStrategySelectProps) {
  const handleChange = (newValue: string) => {
    onChange(newValue as ValuationStrategy);
  };

  return (
    <div className={className}>
      <Select
        value={value}
        onValueChange={handleChange}
        disabled={disabled}
        placeholder="Select strategy..."
      >
        {VALUATION_STRATEGIES.map((strategy) => (
          <Select.Item key={strategy.value} value={strategy.value}>
            {strategy.label}
          </Select.Item>
        ))}
      </Select>
    </div>
  );
}

/**
 * Get display name for a strategy value.
 */
export function getStrategyLabel(value: ValuationStrategy): string {
  const strategy = VALUATION_STRATEGIES.find((s) => s.value === value);
  return strategy?.label ?? value;
}

/**
 * Get description for a strategy value.
 */
export function getStrategyDescription(value: ValuationStrategy): string {
  const strategy = VALUATION_STRATEGIES.find((s) => s.value === value);
  return strategy?.description ?? '';
}

/**
 * Get usage guidance for a strategy value.
 */
export function getStrategyUseWhen(value: ValuationStrategy): string {
  const strategy = VALUATION_STRATEGIES.find((s) => s.value === value);
  return strategy?.useWhen ?? '';
}

/**
 * Get all available strategies for iteration.
 */
export function getAllStrategies() {
  return VALUATION_STRATEGIES;
}
